import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, ClipboardCheck, FlaskConical, Play, ShieldCheck, Target } from 'lucide-react';
import { describeGoal, STORE_LAYOUT_LABELS, type AuditMode } from '@bp/shared';
import { api, qs } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import type { ActionItem, AuditListRow, Goal, Store, TemplateSummary } from '@/lib/types';
import { cn, fmtDate, todayLocal } from '@/lib/utils';
import { Badge, Button, Card, CardHeader, Dialog, Empty, ErrorNote, Field, Input, Loading, PageHeader, Select, Stat } from '@/components/ui';
import { ActionStatusBadge, AuditStatusBadge, ModeBadge, ScoreBadge, StoreStatusBadge } from '@/components/badges';

export function StartAuditDialog({ store, open, onOpenChange }: { store: Store; open: boolean; onOpenChange: (o: boolean) => void }) {
  const { can } = useAuth();
  const nav = useNavigate();
  const templates = useQuery({ queryKey: ['templates', 'usable', store.id], queryFn: () => api.get<TemplateSummary[]>(`/api/templates/usable?store_id=${store.id}`), enabled: open });
  const canOfficial = can('start_official_audit');
  const canPractice = can('start_practice_audit');
  const [mode, setMode] = useState<AuditMode>(canOfficial ? 'official' : 'practice');
  const [templateId, setTemplateId] = useState('');
  const [date, setDate] = useState(todayLocal());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const chosen = templateId || templates.data?.[0]?.id || '';

  async function start() {
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<{ id: string; resumed: boolean }>('/api/audits', { store_id: store.id, template_id: chosen, mode, audit_date: date });
      nav(`/audits/${r.id}`);
    } catch (e) {
      setError(e);
      setBusy(false);
    }
  }

  const ModeCard = ({ value, icon: Icon, title, text, enabled }: { value: AuditMode; icon: typeof ShieldCheck; title: string; text: string; enabled: boolean }) => (
    <button
      type="button"
      disabled={!enabled}
      onClick={() => setMode(value)}
      className={cn('flex-1 rounded-lg border-2 p-3 text-left transition-colors disabled:opacity-40', mode === value ? (value === 'official' ? 'border-info bg-info-bg' : 'border-practice bg-practice-bg') : 'border-border hover:bg-muted')}
    >
      <Icon className={cn('mb-1 size-5', value === 'official' ? 'text-info' : 'text-practice')} />
      <p className="font-semibold">{title}</p>
      <p className="text-xs text-muted-fg">{text}</p>
    </button>
  );

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={`Start an audit — ${store.name}`}
      footer={<><Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button><Button onClick={start} loading={busy} disabled={!chosen}><Play className="size-4" />Start</Button></>}
    >
      <div className="space-y-4">
        <div className="flex gap-3">
          <ModeCard value="official" icon={ShieldCheck} title="Official" text="Scores the store, locks when complete, creates action items." enabled={canOfficial} />
          <ModeCard value="practice" icon={FlaskConical} title="Practice" text="A mock run. Never affects the store's score." enabled={canPractice} />
        </div>
        {templates.isLoading ? <Loading /> : templates.data?.length === 0 ? (
          <p className="text-sm text-muted-fg">No published templates are available for this store.</p>
        ) : (
          <Field label="Template">
            <Select value={chosen} onChange={(e) => setTemplateId(e.target.value)}>
              {templates.data?.map((t) => <option key={t.id} value={t.id}>{t.name} (v{t.current_version_no}, {t.item_count} items)</option>)}
            </Select>
          </Field>
        )}
        <Field label="Audit date"><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} /></Field>
        <p className="text-xs text-muted-fg">If you already have an unfinished audit of this kind here, you'll pick up where you left off.</p>
        <ErrorNote error={error} />
      </div>
    </Dialog>
  );
}

export function StoreDashboardPage() {
  const { id = '' } = useParams();
  const { can, me } = useAuth();
  const [starting, setStarting] = useState(false);
  const store = useQuery({ queryKey: ['store', id], queryFn: () => api.get<Store>(`/api/stores/${id}`) });
  const audits = useQuery({ queryKey: ['audits', { store_id: id }], queryFn: () => api.get<AuditListRow[]>(`/api/audits${qs({ store_id: id, limit: 50 })}`) });
  const tasks = useQuery({ queryKey: ['tasks', { store_id: id }], queryFn: () => api.get<ActionItem[]>(`/api/action-items${qs({ store_id: id, status: 'open' })}`) });
  const goals = useQuery({ queryKey: ['goals', id], queryFn: () => api.get<Goal[]>(`/api/stores/${id}/goals`) });

  if (store.isLoading) return <Loading />;
  if (store.error) return <ErrorNote error={store.error} />;
  const s = store.data!;
  const m = s.metrics;
  const drafts = (audits.data ?? []).filter((a) => a.status === 'in_progress');
  const completed = (audits.data ?? []).filter((a) => a.status === 'completed');
  const lastOfficial = completed.find((a) => a.mode === 'official');
  const activeGoals = (goals.data ?? []).filter((g) => g.active);
  const canStart = can('start_official_audit') || can('start_practice_audit');

  return (
    <>
      <PageHeader
        back={(me?.store_ids?.length ?? 2) > 1 || me?.store_ids === null ? <Link to="/stores" className="mb-2 inline-flex items-center gap-1 text-sm text-muted-fg hover:text-fg"><ChevronLeft className="size-4" />Stores</Link> : undefined}
        title={s.name}
        subtitle={<>#{s.store_number}{s.location && ` · ${s.location}`}{s.region && ` · ${s.region}`}{s.layout !== 'standard' && ` · ${STORE_LAYOUT_LABELS[s.layout]}`}</>}
        actions={canStart && <Button size="lg" onClick={() => setStarting(true)}><ClipboardCheck className="size-5" />Start audit</Button>}
      />
      {starting && <StartAuditDialog store={s} open={starting} onOpenChange={setStarting} />}

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat label="Official score" value={m.latest_score !== null ? `${m.latest_score}%` : '—'} sub={<StoreStatusBadge status={m.status} />} />
        <Stat label="Last official audit" value={<span className="text-lg">{fmtDate(m.last_audit_date)}</span>} sub={`${m.official_audits} official · ${m.practice_audits} practice`} />
        <Stat label="Open action items" value={m.open_action_items} tone={m.open_action_items ? 'warn' : 'neutral'} />
        <Stat label="Overdue" value={m.overdue_action_items} tone={m.overdue_action_items ? 'fail' : 'neutral'} />
      </div>

      {drafts.length > 0 && (
        <Card className="mt-5 border-warn/40">
          <CardHeader title="Unfinished audits" subtitle="Everything is saved as you go — pick up where you left off." />
          <ul className="divide-y">
            {drafts.map((a) => (
              <li key={a.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
                <div className="flex items-center gap-2 text-sm"><ModeBadge mode={a.mode} /><span className="font-medium">{a.template_name}</span><span className="text-muted-fg">· {a.auditor_name} · started {fmtDate(a.audit_date, true)}</span></div>
                <Link to={`/audits/${a.id}`}><Button size="sm" variant="outline"><Play className="size-3.5" />Resume</Button></Link>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <div className="mt-5 grid gap-5 lg:grid-cols-2">
        <Card>
          <CardHeader title="Open action items" action={<Link to={`/tasks?store_id=${s.id}`} className="text-sm text-info hover:underline">All</Link>} />
          {tasks.isLoading ? <Loading /> : (tasks.data?.length ?? 0) === 0 ? <Empty title="No open action items." /> : (
            <ul className="divide-y">
              {tasks.data!.slice(0, 8).map((t) => (
                <li key={t.id}>
                  <Link to={`/tasks/${t.id}`} className="block px-4 py-2.5 hover:bg-muted/50">
                    <p className="line-clamp-1 text-sm font-medium">{t.title}</p>
                    <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-fg">
                      <ActionStatusBadge status={t.status} overdueDays={t.overdue_days} />
                      <span>{t.assignee_name || 'Unassigned'}</span>
                      {t.due_date && <span>· due {fmtDate(t.due_date, true)}</span>}
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card>
          <CardHeader title="Goals" subtitle={lastOfficial ? `Against the ${fmtDate(lastOfficial.audit_date)} official audit` : 'Set by your regional manager'} action={can('manage_goals') && <Link to={`/admin/stores/${s.id}?tab=goals`} className="text-sm text-info hover:underline">Edit</Link>} />
          {activeGoals.length === 0 ? <Empty icon={<Target className="size-7" />} title="No goals set for this store." /> : (
            <ul className="divide-y">
              {activeGoals.map((g) => {
                const r = lastOfficial?.goal_results.find((x) => x.goal_id === g.id);
                return (
                  <li key={g.id} className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm">
                    <span>{describeGoal(g)}</span>
                    {r ? <Badge tone={r.met ? 'pass' : 'fail'}>{r.met ? 'Met' : 'Missed'} · {r.actual}{r.metric === 'max_failed_items' ? ' fails' : '%'}</Badge> : <Badge>Not measured yet</Badge>}
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
      </div>

      <Card className="mt-5">
        <CardHeader title="Audit history" action={<Link to={`/audits?store_id=${s.id}`} className="text-sm text-info hover:underline">All</Link>} />
        {completed.length === 0 ? <Empty title="No completed audits yet." /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-muted-fg uppercase"><tr className="border-b"><th className="px-4 py-2 font-medium">Date</th><th className="px-4 py-2 font-medium">Type</th><th className="px-4 py-2 font-medium">Template</th><th className="px-4 py-2 font-medium">Auditor</th><th className="px-4 py-2 font-medium">Score</th><th className="px-4 py-2 font-medium">Fails</th><th /></tr></thead>
              <tbody>
                {completed.slice(0, 12).map((a) => (
                  <tr key={a.id} className="border-b last:border-0 hover:bg-muted/50">
                    <td className="px-4 py-2.5 whitespace-nowrap"><Link to={`/audits/${a.id}`} className="font-medium hover:underline">{fmtDate(a.audit_date)}</Link></td>
                    <td className="px-4 py-2.5"><ModeBadge mode={a.mode} /></td>
                    <td className="px-4 py-2.5">{a.template_name} <span className="text-xs text-muted-fg">v{a.version_no}</span></td>
                    <td className="px-4 py-2.5">{a.auditor_name}</td>
                    <td className="px-4 py-2.5"><ScoreBadge score={a.score} /></td>
                    <td className="px-4 py-2.5 tabular">{a.failed_items}</td>
                    <td className="px-4 py-2.5"><AuditStatusBadge status={a.status} locked={a.locked} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}
